import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { TableauEmbedding } from "@trinity/chat-components/components/TableauEmbedding/TableauEmbedding.js";
import { PageRenderer } from "@trinity/chat-components/components/PageDashboard/PageRenderer/index.js";
import { useProjectDashboardPage } from "@trinity/chat-components/hooks";
import { useChatApiClient } from "@trinity/chat-components/containers/Chat/useApiClient.js";
import { useActiveBrandIdentifier } from "@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js";
import { useAppSelector } from "@/store";
import { isProdEnv, isSandboxEnv } from "@/config/utils";
import styles from "./style/Dashboard.module.css";

type RouteParams = {
  projectId: string;
};

export const ProjectDashboard = () => {
  const { projectId: projectIdStr } = useParams<RouteParams>();
  const parsed = projectIdStr ? parseInt(projectIdStr, 10) : NaN;
  const projectId = isNaN(parsed) ? null : parsed;
  const groupId = useAppSelector((state) => state.group.id);
  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifier();

  const { data: dashboardPageData, isLoading } = useProjectDashboardPage(projectId);

  const proxyFetch = useCallback(
    ({ agentId, extensionPath }: { agentId: number; extensionPath: string }) =>
      apiClient.pages.proxyWidgetData(agentId, extensionPath, brandIdentifier ?? undefined),
    [apiClient, brandIdentifier],
  );

  // When a page is linked to this project, render it via PageRenderer.
  const pageId = dashboardPageData?.pageId ?? null;

  const legacyDisplay = useMemo(() => {
    if (isProdEnv() && window.location.pathname.includes("projects/1")) {
      return FigmaDashboard("https://cosmic-fake-36139009.figma.site");
    }
    if (isSandboxEnv()) {
      if (groupId === 222) {
        return FigmaDashboard("https://cosmic-fake-36139009.figma.site");
      } else if (groupId === 218) {
        return FigmaDashboard("https://memo-crank-73582957.figma.site/");
      } else if (groupId === 229) {
        if (window.location.pathname.includes("projects/10")) {
          return Iframe(
            "https://dmi-staging.trinitylifesciences.com/umbraco/api/authentication/GetTempLoginToken/?apiKey=1234&memberId=4564&returnUrl=/organizations/demo-intelligenceedge/dashboards/abc-pharmas-dashboard-1/?indication=339dad8c-1690-4287-b52b-f84654ba3a44",
            40,
          );
        } else {
          return Iframe(
            "https://dmi-staging.trinitylifesciences.com/umbraco/api/authentication/GetTempLoginToken/?apiKey=1234&memberId=4564&returnUrl=/organizations/demo-intelligenceedge/dashboards/abc-pharmas-dashboard-1/clinical-trials/?indication=339dad8c-1690-4287-b52b-f84654ba3a44",
            20,
          );
        }
      }
    }
    return (
      <TableauEmbedding displayUrl="https://public.tableau.com/views/FIDashboard_17581440680690/Dashboard2?:language=en-US&:sid=&:redirect=auth&:display_count=n&:origin=viz_share_link" />
    );
  }, [groupId]);

  // Render the linked Page if one is configured; otherwise fall back to legacy hardcoded dashboards.
  const dashboardDisplay = isLoading
    ? null
    : pageId !== null
      ? <PageRenderer pageId={pageId} proxyFetch={proxyFetch} brandIdentifier={brandIdentifier ?? undefined} />
      : legacyDisplay;

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <div className={styles.section}>{dashboardDisplay}</div>
      </div>
    </div>
  );
};

const Iframe = (url: string, topPadding: number) => {
  const leftNavWidth = 62;
  const topNavHeight = 60;
  const topHeaderHeight = 148;

  const topNavHeightTotal = topNavHeight + topHeaderHeight;

  return (
    <div
      style={{
        position: "relative",
        width: `calc(100% + ${leftNavWidth}px)`,
        height: `100%`,
        overflow: "hidden",
        paddingTop: `${topPadding}px`,
      }}
    >
      <iframe
        src={url}
        style={{
          position: "absolute",
          top: `calc(-${topNavHeightTotal}px + ${topPadding}px)`,
          left: `-${leftNavWidth}px`,
          width: `100%`,
          height: `calc(100% + ${topNavHeightTotal}px - ${topPadding}px)`,
          border: "none",
          boxSizing: "border-box",
        }}
        title="Dashboard"
      />
    </div>
  );
};

const FigmaDashboard = (url: string) => {
  return (
    <iframe
      src={url}
      style={{
        width: "100%",
        height: "calc(100% - var(--header-height))",
        maxHeight:
          "calc(100vh - var(--navbar-height) - var(--header-height) - 40px)",
        border: "none",
        padding: "15px",
        boxSizing: "border-box",
      }}
      title="Figma Dashboard"
    />
  );
};
